// Sending handling-time measurements to the server, and the rules about when.
//
// FIRE AND FORGET, ALWAYS. Nothing on screen waits for this and nothing retries
// it. Every call site ignores the result and every failure is swallowed — a
// reviewer closing a modal must never see an error because a stopwatch could
// not be filed. That is a deliberate trade: the measure is allowed to be
// slightly incomplete, the screen is not allowed to be wrong.
//
// ONE POST PER BURST. Sessions queue and flush on the next tick, so a bulk
// decision over twenty invoices is one request rather than twenty. The queue is
// also flushed when the page goes away, which is the case a plain `fetch` loses
// — see `keepalive` in `api/http.ts`.
//
// NO POLLING, NO HEARTBEAT, NO TIMER. The accumulator in `handlingTimer.ts`
// works out idle time retrospectively, so nothing here runs while a modal is
// open. The cost of the whole feature is one request per invoice viewed.

import { getClient } from '../api/client'
import { DecisionStep, InvoiceStatus } from './constants'
import type { ViewSessionInput } from '../api/types'
import { HandlingTimer, newSessionId, type HandlingSpan } from './handlingTimer'

/** Sessions waiting to be sent. Drained whole by `flush`. */
let pending: ViewSessionInput[] = []
let scheduled = false
let unloadBound = false

/**
 * The server's cap. A burst larger than this is split across requests rather
 * than rejected — twenty over the line should not cost the hundred below it.
 */
const MAX_PER_REQUEST = 100

function bindUnload(): void {
  if (unloadBound || typeof window === 'undefined') return
  unloadBound = true
  // `pagehide` rather than `beforeunload`: it fires for a tab being closed AND
  // for a page entering the back/forward cache, and it does not suppress the
  // browser's own fast navigations the way a `beforeunload` handler can.
  window.addEventListener('pagehide', () => {
    flushViewSessions()
  })
}

function flushViewSessions(): void {
  scheduled = false
  if (pending.length === 0) return
  const batch = pending
  pending = []
  for (let at = 0; at < batch.length; at += MAX_PER_REQUEST) {
    const slice = batch.slice(at, at + MAX_PER_REQUEST)
    // No `await`, and the rejection is swallowed rather than logged: a failed
    // measurement is not an event anyone acts on, and a console error on every
    // offline modal close is noise that hides real ones.
    void getClient()
      .recordViewSessions(slice)
      .catch(() => undefined)
  }
}

function enqueue(session: ViewSessionInput): void {
  bindUnload()
  pending.push(session)
  if (scheduled) return
  scheduled = true
  // A macrotask, not a microtask: a bulk decision enqueues inside a loop that
  // is itself inside an async handler, and a microtask would fire between
  // iterations and defeat the batching.
  setTimeout(flushViewSessions, 0)
}

/** Queue a finished viewing session. */
export function recordViewSession(
  invoiceId: string,
  status: InvoiceStatus,
  span: HandlingSpan,
): void {
  enqueue({
    client_session_id: newSessionId(),
    invoice_id: invoiceId,
    status,
    kind: 'view',
    ...span,
  })
}

/**
 * The status an invoice MUST be at to receive a decision at each step.
 *
 * A bulk decision names a step and a list of ids, never a status, so this is
 * how a bulk touch learns where it happened. Typed as a TOTAL record, so a
 * new decision step fails to compile here rather than quietly dropping its
 * bulk work out of the measure.
 *
 * Closing Confirmation is the one that does not read off its own name: it acts
 * on `Pending Export`, because the export happens before the confirmation.
 */
const STATUS_AT_STEP: Readonly<Record<DecisionStep, InvoiceStatus>> = {
  [DecisionStep.ImportControl]: InvoiceStatus.PendingImportControl,
  [DecisionStep.Controller]: InvoiceStatus.PendingController,
  [DecisionStep.Department]: InvoiceStatus.PendingDepartment,
  [DecisionStep.Director]: InvoiceStatus.PendingDirector,
  [DecisionStep.Oversight]: InvoiceStatus.PendingOversight,
  [DecisionStep.ExportControl]: InvoiceStatus.PendingExportControl,
  [DecisionStep.ClosingConfirmation]: InvoiceStatus.PendingExport,
  [DecisionStep.DenialReview]: InvoiceStatus.PendingDenialReview,
}

/**
 * Queue a zero-second touch for each invoice in a bulk decision.
 *
 * WITHOUT THIS, THE FASTEST PATH THROUGH THE TOOL WOULD BE INVISIBLE. Twenty
 * invoices approved from a queue are twenty invoices handled in a few seconds
 * total, and a measure that only counted opened modals would report them as no
 * data — making bulk approval look like a gap in the instrumentation rather
 * than the thing the tool does best.
 *
 * `at` is passed rather than read from the clock so a caller can stamp a whole
 * batch with one instant.
 */
export function recordBulkTouches(
  invoiceIds: readonly string[],
  step: DecisionStep,
  at: Date = new Date(),
): void {
  const stamp = at.toISOString()
  const status = STATUS_AT_STEP[step]
  for (const invoiceId of invoiceIds) {
    enqueue({
      client_session_id: newSessionId(),
      invoice_id: invoiceId,
      status,
      kind: 'bulk_action',
      opened_at: stamp,
      ended_at: stamp,
      active_seconds: 0,
      idle_seconds: 0,
    })
  }
}

/** Send anything queued right now. Exported for the tests and the unload path. */
export function flushNow(): void {
  flushViewSessions()
}

export { HandlingTimer }
